import { useEffect, useState } from 'react';

import { FlagEventBus } from './FlagEventBus.ts';

/**
 * Use Notification State
 *
 * This hook provides react bindings for the `FlagEventBus.
 * By registering this hook, you'll receive state updates
 * each time notifications are enabled or disabled as the
 * result of specific user behaviors such typing into an editor
 * or putting Confluence into a background tab
 *
 * When writing features that consume the flags package, you
 * can use this hook to decide appropriate times to show
 * notifications to Confluence users.
 *
 * Example usage:
 * ```
 * export const FeatureWithNotification = (props) => {
 *   const canShowNotifications = useFlagEventBus();
 *   useEffect(() => {
 *     if(canShowNotifications) {
 *       props.flagStateContainer.showInfoFlag()
 *     } else {
 *       delayUntilCanShowNotificationsIsTrue()
 *     }
 *    }, []);
 * }
 * ```
 */
export const useNotificationState = (): boolean => {
	const [active, setActive] = useState(true);
	useEffect(() => {
		const ID1 = FlagEventBus.on('enable-notifications', () => {
			setActive(true);
		});
		const ID2 = FlagEventBus.on('disable-notifications', () => {
			setActive(false);
		});
		return () => {
			FlagEventBus.off('enable-notifications', ID1);
			FlagEventBus.off('disable-notifications', ID2);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return active;
};
